<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <client-only>
    <transition name="slide">
      <product-tabs-bar v-show="isSectionTabsShown" v-model="activatedTab" class="z-10" :sections="sections" @tab-change="handleTabChange" />
    </transition>
  </client-only>

  <branch-detail-provider
    id="branch-page"
    v-model:attendance="attendance"
    v-model:date="date"
    v-model:slot-info="slotInfo"
    :branch="branch"
    :is-sold-out="isSoldOut"
    class="bg-gray-20 pb-6 sm:pt-6 sm:pb-10 text-base"
  >
    <div class="container mx-auto">
      <div class="flex justify-center relative gap-6">
        <div class="branch-info w-full flex flex-col gap-2 md:gap-6 overflow-auto">
          <template v-for="section in sections" :key="section.id">
            <div
              v-if="section.show && section.component"
              :id="section.id"
              class="section-wrapper scroll-mt-32"
              :class="[section.label ? TOP_TAB_CLASS : '', section.class]"
            >
              <component :is="section.component" v-bind="section.props" />
            </div>
          </template>
        </div>

        <product-sidebar
          v-if="branch && appStore.isLargeDesktopScreen"
          :class="isSectionTabsShown ? 'max-h-[calc(100dvh-166px)] top-152px' : 'max-h-[calc(100dvh-112px)] top-22'"
          :branch="branch"
          :arrival-times="arrivalTimes"
        />
      </div>
    </div>

    <product-mobile-footer-bar v-if="branch && !appStore.isLargeDesktopScreen" class="z-10" />
  </branch-detail-provider>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { MaybeElement, UseIntersectionObserverReturn } from '@vueuse/core'
import { format } from 'date-fns'
import { AsyncDataKeys, getAsyncDataKey } from '@/constants/async-data-keys'

import { getBranchMenuItem } from '@/service/branch'

import { useAppStore } from '@/store/app'
import { useBranchStore } from '@/store/branch'
import { useGlobalModalStore } from '@/store/global-modal'

import BranchDetailProvider from '@/providers/branch-detail-provider.vue'

import ReservationUnavailableModalContent from '@/components/reservation/reservation-unavailable-modal-content.vue'
import ProductSidebar from '@/components/product/product-detail/product-sidebar.vue'
import ProductMobileFooterBar from '@/components/product/product-detail/product-mobile-footer-bar.vue'
import ProductTabsBar from '@/components/product/product-detail/product-tabs-bar.vue'
import ProductSectionGallery from '@/components/product/product-detail/product-section-gallery.vue'
import ProductSectionReservation from '@/components/product/product-detail/product-section-reservation.vue'
import ProductSectionRecommendedMenu from '@/components/product/product-detail/product-section-recommended-menu.vue'
import ProductSectionComments from '@/components/product/product-detail/product-section-comments.vue'
import ProductSectionAbout from '@/components/product/product-detail/product-section-about.vue'
import ProductSectionSpecialCondition from '@/components/product/product-detail/product-section-special-condition.vue'
import ProductSectionSimilarCategories from '@/components/product/product-detail/product-section-similar-categories.vue'

import type { GetProductCommentsParams, SlotInfo } from '@/types/product'
import type { ModalState } from '@/types/commons'
import { GtmEvent } from '@/types/gtm'

import { DEFAULT_RESTAURANT_ATTENDANCE, QueryStringKeys } from '@/constants/commons'

const nuxtApp = useNuxtApp()

const { t } = useI18n()

const route = useRoute()
const router = useLocaleRouter()
const { backPath } = useRouterState()

const branchId = computed(() => route.params.bid as string)
const routeSlot = computed(() => route.query[QueryStringKeys.SLOT] as string | undefined)

const appStore = useAppStore()
const branchStore = useBranchStore()
const globalModalStore = useGlobalModalStore()

const { arrivalTimes } = storeToRefs(branchStore)

const { isLoading: isPageIndicatorLoading } = useLoadingIndicator()

const pid = computed(() => branch.value?.pid)

// fetch api data
const productCommentsQueryParams = computed<GetProductCommentsParams>(() => ({
  start: 0,
  size: 10,
  sortby: 'default',
}))

const { data: branchData, error: branchDetailError } = await useDualAsyncData(
  getAsyncDataKey(AsyncDataKeys.BRANCH_DETAIL, [branchId.value]),
  async () => {
    if (!branchId.value) return null
    const branchRes = await branchStore.fetchBranch(branchId.value)
    return branchRes
  }
)

// Convert undefined to null for type compatibility with Nullable<BranchData>
const branch = computed(() => branchData.value ?? null)

if (branchDetailError.value) {
  throwNotFoundError(import.meta.client)
}

const branchMenuItemPromise = useAsyncData(getAsyncDataKey(AsyncDataKeys.BRANCH_MENU_ITEM, [branchId.value]), async () => {
  const { data } = await getBranchMenuItem(branchId.value)

  return data
})

const productCommentsPromise = useGetProductComments('branch-detail-comments', pid, productCommentsQueryParams, { useDual: true })

const branchHeadcountPromise = useAsyncData(getAsyncDataKey(AsyncDataKeys.BRANCH_HEADCOUNT, [branchId.value, pid.value]), () => {
  if (!pid.value) {
    throw new Error('pid is required')
  }

  return branchStore.fetchHeadcount(branchId.value, pid.value)
})

const [{ data: branchMenuItem }, { data: productComments }] = await Promise.all([
  branchMenuItemPromise,
  productCommentsPromise,
  branchHeadcountPromise,
])

const attendance = ref(branchStore.headcountInfo?.default || DEFAULT_RESTAURANT_ATTENDANCE)

await useGetBranchArrivalTimes('branch-detail', { branch, attendance, callback: branchStore.setArrivalTimes })

const isSoldOut = computed(() => branch.value?.onboard === 0)
const availableSlotFromRoute = computed(() => {
  return (arrivalTimes.value?.slots?.find(slotInfo => slotInfo.slot === routeSlot.value) as SlotInfo) || null
})
const availableDate = computed(() => {
  if (availableSlotFromRoute.value) {
    return format(new Date(routeSlot.value!), 'yyyy-MM-dd')
  }

  const firstAvailableDate = arrivalTimes.value?.slots?.[0].slot?.split(' ')[0]

  return firstAvailableDate || null
})

const date = ref(availableDate.value)
const slotInfo = ref<Nullable<SlotInfo>>(availableSlotFromRoute.value)

useEtgSeoSetting('branch', { branch, productComments })

const sections = computed(() => {
  if (!branch?.value) return []

  return [
    {
      id: 'product-detail-section-gallery',
      component: ProductSectionGallery,
      show: !!branch?.value?.images?.length,
      props: {
        class: '-mb-2 md:mb-0',
      },
    },
    {
      id: 'product-detail-section-name',
      component: ProductSectionReservation,
      show: !appStore.isLargeDesktopScreen,
      class: 'bg-gray-10 pb-4',
      props: {
        arrivalTimes: arrivalTimes.value,
      },
    },
    {
      id: 'product-detail-section-recommended-menu',
      component: ProductSectionRecommendedMenu,
      show: !isEmpty(branchMenuItem.value),
      label: t('branch_detail.tab.menu'),
      props: {
        branchMenuItem: branchMenuItem.value,
        selectedDiscount: slotInfo.value?.discount,
      },
    },
    {
      id: 'product-detail-section-comments',
      component: ProductSectionComments,
      show: !!productComments.value?.comments?.length,
      label: t('branch_detail.tab.review'),
      props: {
        productComments: productComments.value,
        pid: pid.value,
      },
    },
    {
      id: 'product-detail-section-about',
      component: ProductSectionAbout,
      show: !!branch.value,
      label: t('branch_detail.tab.about'),
    },
    {
      id: 'product-detail-section-notices',
      component: ProductSectionSpecialCondition,
      show: !!branch.value?.special_condition || !!branch.value?.customer_notice || !!branch.value?.reschedule_rules,
      label: t('branch_detail.tab.condition'),
    },
    {
      id: 'product-detail-section-similar-categories',
      component: ProductSectionSimilarCategories,
      show: !!branch.value?.tags?.length,
    },
  ]
})

function showReservationUnavailableModal(text: string, args: Omit<ModalState, 'body'> = {}) {
  globalModalStore.showModal({ body: h(ReservationUnavailableModalContent, { text }), maskClosable: false, ...args })
}

function handleReservationUnavailableIfNeeded() {
  if (isSoldOut.value) {
    showReservationUnavailableModal(t('branch_detail.dialog.disabled'), {
      onConfirm: () => {
        // spec: https://www.figma.com/design/H8XF891oLkmXTTsE1fS0t5/eatigo-WEB?node-id=10278-111655&node-type=instance&m=dev
        router.replace(backPath.value || '/')
      },
    })
    return
  }

  // 當從列表頁點擊「時段按鈕」進入分店頁時，若已無指定時段，則顯示對應的 modal
  if (routeSlot.value && !availableSlotFromRoute.value) {
    showReservationUnavailableModal(t('reservation.dialog.unavailable'))
  }
}

// 下滑一段高度後，顯示 section tabs
const { y } = useWindowScroll()
const isSectionTabsShown = computed(() => {
  if (import.meta.server || isPageIndicatorLoading.value) return false

  return y.value > 256
})

// 處理畫面 scroll 時，調整當前在畫面被 highlight 的 section tab
const activatedTab = ref('product-detail-section-basic-info')
const isAnimating = ref(false)

const TOP_TAB_CLASS = 'product-detail-sections_in-top-tab'

const tabSections = ref<MaybeElement[]>([])
const observer = ref<UseIntersectionObserverReturn | null>(null)

const createIntersectionObserver = () => {
  const windowHeight = appStore.windowHeight

  const marginTop = 64 + 64 // header + sticky tabs bar
  const marginBottom = Math.max(windowHeight - marginTop - 8, 0) // 1/2 gap, gap for better UX

  const options = {
    root: null,
    rootMargin: `-${marginTop}px 0px -${marginBottom}px 0px`,
    threshold: 0,
  }
  return useIntersectionObserver(
    tabSections,
    entries => {
      entries.forEach(entry => {
        if (isAnimating.value || !entry.isIntersecting) return
        activatedTab.value = entry.target.id
      })
    },
    options
  )
}

const renewObserver = () => {
  observer.value?.stop()
  observer.value = createIntersectionObserver()
}

onMounted(() => {
  tabSections.value = Array.from(document.querySelectorAll('.section-wrapper')) as MaybeElement[]

  nuxtApp.$trackEvent({
    event: GtmEvent.RESTAURANT_DETAIL_VIEWED,
    payload: {
      branch: branch.value,
    },
  })
  watch(isSoldOut, handleReservationUnavailableIfNeeded, { immediate: true })
  watch(() => appStore.windowHeight, renewObserver, { immediate: true })
})

// 處理點擊 tab 時， scroll 到指定元素過程中觸發 Intersection Observer 的事件
const handleTabChange = () => {
  isAnimating.value = true
  debounceResetAnimating()
}
const debounceResetAnimating = useDebounceFn(() => {
  isAnimating.value = false
}, 1500)
</script>
